import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from "@angular/core";
import { FormBuilder, FormGroup } from "@angular/forms";
import { TranslateService } from "@ngx-translate/core";
import moment from "moment";
import { AssessmentDataService } from "../../../providers/asmt-data-service/asmt-data-service";

@Component({
  selector: "pain-screening-tool",
  templateUrl: "pain-screening-tool.html",
})
export class PainScreeningToolComponent {
  @Input() assessmentId: string;
  @Input() sectionId: string;
  @Input() config;
  @Input() disabled: boolean = false;
  @Input() patientAdmittedDate: string = "";
  @Input() dateFirstPainScreening: string = "";
  @Input() isINV: boolean = false;

  @Output() onDataUpdate: EventEmitter<any> = new EventEmitter();
  @Output() onCardInitialized: EventEmitter<any> = new EventEmitter();
  painScreen: boolean = false;

  @ViewChild("buttonContainer") buttons;

  id: string;
  uuid: string;
  componentId: string;
  label: string;
  declinable: boolean;

  tools: string[] = [];
  inputs: any = {};
  currentTool: string = null;
  values: object = {};
  scrollRate: number = 25; // Percent of tool bar to scroll on click
  scrollAmount: number;
  readonly: boolean = false;
  initialized: boolean = false;
  abbeyData: object = { tool: "abbey", values: null };
  wongBakerData: object = { tool: "wongBaker", values: null };
  numericData: object = { tool: "numeric", values: null };
  verbalData: object = { tool: "verbal", values: null };
  flaccData: object = { tool: "flacc", values: null };
  painADData: object = { tool: "painAD", values: null };
  formGroup: FormGroup;
  existingData: object;
  inputKeys: string[];
  emitters: object;
  painTool: object;
  painValues: object;
  disableTool: boolean = false;

  hisV2Toggle: boolean = false;
  layout: any;
  depCounter: number = 0;
  depMap: object = {};

  constructor(
    public translatePipe: TranslateService,
    private cdr: ChangeDetectorRef,
    public formBuilder: FormBuilder,
    public dataService: AssessmentDataService
  ) {}

  ngOnInit(): void {
    this.id = this.config.id;
    this.inputs = this.config.inputs;
    this.componentId = this.config.componentId;
    this.label =
      this.config.label !== undefined
        ? this.config.label
        : this.config.cardName;
    this.tools = this.config.tools;
    this.declinable = this.config.declinable;
    this.readonly = this.config.readonly;

    if (this.config && this.config.hisV2) {
      this.hisV2Toggle = this.config.hisV2;
      if (this.config.inputs && this.config.inputs["dateFirstPainScreening"]) {
        this.config.inputs["dateFirstPainScreening"].patientAdmittedDate =
          this.patientAdmittedDate;
      }
    }

    this.inputKeys = Object.keys(this.inputs);
    for (let key of this.inputKeys) {
      this.config.inputs[key].id = key;
      if (this.config.inputs[key].type === undefined) {
        let index = this.inputKeys.indexOf(key);
        this.inputKeys.splice(index, 1);
      }
    }

    this.formGroup = this.formBuilder.group({});
    for (let key of this.inputKeys) {
      if (this.hisV2Toggle) {
        if (key === "dateFirstPainScreening") {
          this.formGroup.addControl(
            key,
            this.formBuilder.control({ value: null, disabled: true })
          );
        } else {
          this.formGroup.addControl(key, this.formBuilder.control(undefined));
        }
      } else {
        this.formGroup.addControl(key, this.formBuilder.control(undefined));
      }
    }
    this.disableSymptomFields();

    this.formGroup.valueChanges.subscribe((data) => {
      this.values["experiencingPainQuestion"] = data.experiencingPainQuestion;
      this.values["dateFirstPainScreening"] = data.dateFirstPainScreening;
      if (data.patientHasNeuropathicPain) {
        this.values["patientHasNeuropathicPain"] =
          data.patientHasNeuropathicPain;
      }
      if (data.rankSymptomImpact) {
        this.values["rankSymptomImpact"] = data.rankSymptomImpact;
      }
      if (data.explainSymptomImpact) {
        this.values["explainSymptomImpact"] = data.explainSymptomImpact;
      }
      if (data.otherSymptomArea) {
        this.values["otherSymptomArea"] = data.otherSymptomArea;
      }
      if (data.explanation) {
        this.values["explanation"] = data.explanation;
      }
      this.onDataUpdate.emit({ cardId: this.id, value: this.values });
    });

    if (this.existingData) {
      for (let key of Object.keys(this.existingData)) {
        if (this.formGroup.controls[key]) {
          this.formGroup.controls[key].setValue(this.existingData[key]);
        }
      }
    }
    if (this.hisV2Toggle) {
      if (
        this.config.inputs &&
        this.config.inputs["experiencingPainQuestion"]
      ) {
        this.onChanges();
      }
      if (this.config.inputs && this.config.inputs["rankSymptomImpact"]) {
        this.subscribeToSymptomImpactChanges();
      }
    }
  }

  subscribeToSymptomImpactChanges(): void {
    this.formGroup.controls["rankSymptomImpact"].valueChanges.subscribe(
      (data) => {
        const shouldEnableFields = [
          "notImpacted",
          "patientNotExperiencingTheSymptom",
        ].includes(data);
        if (shouldEnableFields) {
          this.disableSymptomFields();
        } else {
          this.enableSymptomFields();
        }
      }
    );
  }

  enableSymptomFields(): void {
    this.formGroup.controls["explainSymptomImpact"].enable();
    this.formGroup.controls["otherSymptomArea"].enable();
  }
  disableSymptomFields(): void {
    if (this.config.inputs.rankSymptomImpact) {
      this.formGroup.controls["explainSymptomImpact"].setValue(null);
      this.formGroup.controls["otherSymptomArea"].setValue(null);
      this.formGroup.controls["explainSymptomImpact"].disable();
      this.formGroup.controls["otherSymptomArea"].disable();
      this.values["otherSymptomArea"] = null;
    }
  }

  // NOVA-1948: clear the pain-rating tool + severity so a "not in pain / not
  // screened" answer cannot leave a tool/severity behind — the contradictory
  // saved state (experiencingPainQuestion="no" alongside painTool/severity)
  // that drove the SUP-19896 HOPE mis-coding. Clears only the pain-rating
  // fields; leaves experiencingPainQuestion and the symptom/neuropathic
  // fields untouched. Mirrors the tool-clearing subset of clearAll().
  clearPainToolData(): void {
    this.abbeyData["values"] = null;
    delete this.values["abbey"];
    this.flaccData["values"] = null;
    delete this.values["flacc"];
    this.numericData["values"] = null;
    delete this.values["numeric"];
    this.verbalData["values"] = null;
    delete this.values["verbal"];
    this.painADData["values"] = null;
    delete this.values["painAD"];
    this.wongBakerData["values"] = null;
    delete this.values["wongBaker"];
    delete this.values["painTool"];
    delete this.values["painValues"];
    delete this.values["severity"];
    this.painValues = null;
    this.currentTool = "";
    this.painScreen = false;
    this.disableTool = false;
    this.cdr.detectChanges();
  }

  onChanges(): void {
    this.formGroup
      .get("experiencingPainQuestion")
      .valueChanges.subscribe((data) => {
        let value = "";
        if (data) {
          value = data;
        }
        if (value === "yes") {
          if (this.dateFirstPainScreening) {
            this.formGroup.controls["dateFirstPainScreening"].setValue({
              date: this.dateFirstPainScreening,
            });
          } else if (this.formGroup.controls["dateFirstPainScreening"].value) {
            //DO NOTHING - keep existing date
          } else {
            this.formGroup.controls["dateFirstPainScreening"].setValue({
              date: moment().format("YYYYMMDD"),
            });
          }
          this.formGroup.controls["dateFirstPainScreening"].enable();
        } else if (value === "no") {
          // NOVA-1948: clear tool + severity FIRST so the severity-driven
          // "force experiencingPainQuestion = yes" logic (dataUpdate / data
          // setter) can't re-fire, then emit the cleaned card so the stored
          // data_json can't hold a tool/severity alongside a "no" answer.
          this.clearPainToolData();
          this.formGroup.controls["dateFirstPainScreening"].setValue(null);
          this.dateFirstPainScreening = null;
          this.formGroup.controls["dateFirstPainScreening"].disable();
          this.onDataUpdate.emit({ cardId: this.id, value: this.values });
        }
      });
  }

  ngAfterContentInit(): void {
    if (this.tools.length) {
      this.scrollRate = 100 / this.tools.length;
    }
    this.scrollAmount =
      this.buttons.nativeElement.scrollWidth * (this.scrollRate / 100);
    this.cdr.detectChanges();
  }

  @Input() set data(incomingData: any) {
    if (incomingData) {
      let cardData = incomingData;
      if (cardData.data) {
        this.values = cardData.data[0];
        this.uuid = this.values["id"];
        if (
          this.inputs &&
          this.inputs.experiencingPainQuestion &&
          this.values["experiencingPainQuestion"]
        ) {
          this.formGroup.controls["experiencingPainQuestion"].setValue(
            this.values["experiencingPainQuestion"]
          );
        }
        if (
          this.inputs &&
          this.inputs.patientHasNeuropathicPain &&
          this.values["patientHasNeuropathicPain"]
        ) {
          this.formGroup.controls["patientHasNeuropathicPain"].setValue(
            this.values["patientHasNeuropathicPain"]
          );
        }
        if (this.values["rankSymptomImpact"]) {
          let incomingSymptom = JSON.parse(JSON.stringify(this.values));
          this.formGroup.controls["rankSymptomImpact"].setValue(
            incomingSymptom["rankSymptomImpact"]
          );
          this.formGroup.controls["explainSymptomImpact"].setValue(
            incomingSymptom["explainSymptomImpact"]
          );
          this.formGroup.controls["otherSymptomArea"].setValue(
            incomingSymptom["otherSymptomArea"]
          );
        }
        if (this.values["explanation"]) {
          let incomingSymptom = JSON.parse(JSON.stringify(this.values));
          this.formGroup.controls["explanation"].setValue(
            incomingSymptom["explanation"]
          );
        }
        this.currentTool = this.values["painTool"];
        if (this.currentTool === "flacc") {
          this.flaccData["values"] = this.values["painValues"];
        } else if (this.currentTool === "wongBaker") {
          this.wongBakerData["values"] = this.values["painValues"];
        } else if (this.currentTool === "numeric") {
          this.numericData["values"] = this.values["painValues"];
        } else if (this.currentTool === "abbey") {
          this.abbeyData["values"] = this.values["painValues"];
        } else if (this.currentTool === "painAD") {
          this.painADData["values"] = this.values["painValues"];
        } else if (this.currentTool === "verbal") {
          this.verbalData["values"] = this.values["painValues"];
        }
        this.painValues = this.values["painValues"];

        if (
          this.hisV2Toggle &&
          this.inputs &&
          this.inputs.experiencingPainQuestion &&
          this.values["experiencingPainQuestion"] != null &&
          this.values["experiencingPainQuestion"].toString() != "no"
        ) {
          if (null != this.values["severity"]) {
            this.formGroup.controls["experiencingPainQuestion"].setValue("yes");
          } else if (
            this.values["experiencingPainQuestion"] == null ||
            (this.values["experiencingPainQuestion"] != null &&
              this.values["experiencingPainQuestion"].toString() != "yes")
          ) {
            this.formGroup.controls["experiencingPainQuestion"].setValue(null);
            this.formGroup.controls["dateFirstPainScreening"].setValue(null);
          }
        }

        if (
          null != this.values["severity"] &&
          this.values["severity"] > 0 &&
          this.isINV
        ) {
          this.painScreen = true;
        } else {
          this.painScreen = false;
        }
      }
    }

    if (incomingData !== undefined && !this.initialized) {
      this.initialized = true;
      this.onCardInitialized.emit({ cardId: this.id, value: this.values });
    }
  }

  toolSelected(tool: string) {
    this.abbeyData["values"] = null;
    delete this.values["abbey"];
    this.flaccData["values"] = null;
    delete this.values["flacc"];
    this.numericData["values"] = null;
    delete this.values["numeric"];
    this.verbalData["values"] = null;
    delete this.values["verbal"];
    this.painADData["values"] = null;
    delete this.values["painAD"];
    this.wongBakerData["values"] = null;
    delete this.values["wongBaker"];
    this.values["severity"] = null;
    this.currentTool = tool;
    this.cdr.detectChanges();
    this.disableTool = true;
  }

  dataUpdate(data: object) {
    this.values["painValues"] = data["values"];
    this.values["painTool"] = this.currentTool;
    this.values["severity"] = data["severity"];

    if (
      this.hisV2Toggle &&
      this.inputs &&
      this.inputs.experiencingPainQuestion
    ) {
      if (null != this.values["severity"]) {
        this.formGroup.controls["experiencingPainQuestion"].setValue("yes");
      } else {
        this.formGroup.controls["experiencingPainQuestion"].setValue(null);
        this.formGroup.controls["dateFirstPainScreening"].setValue(null);
      }
    }

    if (
      null != this.values["severity"] &&
      this.values["severity"] > 0 &&
      this.isINV
    ) {
      this.painScreen = true;
    } else {
      this.painScreen = false;
    }
    this.onDataUpdate.emit({ cardId: this.id, value: this.values });
  }

  scrollLeft() {
    this.buttons.nativeElement.scrollLeft -= this.scrollAmount;
  }

  scrollRight() {
    this.buttons.nativeElement.scrollLeft += this.scrollAmount;
  }

  clearAll() {
    this.abbeyData["values"] = null;
    delete this.values["abbey"];
    this.flaccData["values"] = null;
    delete this.values["flacc"];
    this.numericData["values"] = null;
    delete this.values["numeric"];
    this.verbalData["values"] = null;
    delete this.values["verbal"];
    this.painADData["values"] = null;
    delete this.values["painAD"];
    this.wongBakerData["values"] = null;
    delete this.values["wongBaker"];
    delete this.values["painTool"];
    delete this.values["painValues"];
    delete this.values["patientHasNeuropathicPain"];
    delete this.values["dateFirstPainScreening"];
    delete this.values["severity"];
    delete this.values["experiencingPainQuestion"];

    this.values["otherSymptomArea"] = null;

    this.painValues = null;
    this.cdr.detectChanges();
    this.currentTool = "";
    this.cdr.detectChanges();

    if (this.formGroup.controls["patientHasNeuropathicPain"]) {
      this.formGroup.controls["patientHasNeuropathicPain"].setValue(null);
    }

    if (this.formGroup.controls["experiencingPainQuestion"]) {
      this.formGroup.controls["experiencingPainQuestion"].setValue(null);
    }

    this.painScreen = false;
    this.onDataUpdate.emit({ cardId: this.id, value: this.values });

    if (this.formGroup.controls["dateFirstPainScreening"]) {
      this.formGroup.controls["dateFirstPainScreening"].setValue(null);
    }

    this.dateFirstPainScreening = null;
    this.disableTool = false;
  }

  disableTools(tool) {
    if (this.disableTool) {
      if (tool === this.currentTool && this.values["severity"]) {
        return false;
      } else if (tool !== this.currentTool && this.values["severity"]) {
        return true;
      }
    }
  }
}



// WEBPACK FOOTER //
// ./src/components/tools/pain-screening-tool/pain-screening-tool.ts